// Affiliate carrier configuration
// Add new carriers here to make them available in the matching system

export interface AffiliateCarrier {
  id: string;
  name: string;
  referralLink: string;
  description: string;
  logo?: string;
  isPrimary?: boolean;
  talkingPoints?: string[];
  conditions: {
    ageMin?: number;
    ageMax?: number;
    smokingAllowed?: boolean;
    healthStatusRequired?: string[];
    statesAvailable?: string[];
    noMedicalExamAvailable?: boolean;
    policyTypesOffered?: string[];
    specialties?: string[];
  };
  scoring: {
    baseScore: number;
    scoreBoost?: number;
    ageBonus?: { min: number; max: number; bonus: number }[];
    healthBonus?: { status: string; bonus: number }[];
    priorityMatch?: { priority: string; bonus: number }[];
    noMedicalExamBonus?: number;
    instantApprovalBonus?: number;
  };
}

// Primary affiliate carriers
export const AFFILIATE_CARRIERS: AffiliateCarrier[] = [
  {
    id: 'ladder-life',
    name: 'Ladder Life',
    referralLink: 'https://ladder.pxf.io/19NDND',
    description: 'Flexible term life insurance with the ability to adjust coverage online. No medical exam required for most applicants.',
    logo: '/images/carriers/ladder-life.png',
    isPrimary: true,
    talkingPoints: [
      'No medical exam required for most applicants - get covered instantly',
      'Fast 100% online application takes just minutes to complete',
      'Flexible coverage from $100K to $8M that adjusts with your life',
      'Competitive rates designed for healthy non-smokers',
      'Instant coverage decisions powered by advanced underwriting',
      'Cancel anytime with no penalties or hidden fees'
    ],
    conditions: {
      ageMin: 20,
      ageMax: 60,
      smokingAllowed: true,
      healthStatusRequired: ['good', 'average'],
      noMedicalExamAvailable: true,
      policyTypesOffered: ['term', 'not_sure'],
      specialties: ['fast_approval', 'no_medical_exam', 'flexible_coverage']
    },
    scoring: {
      baseScore: 85,
      scoreBoost: 100,
      ageBonus: [
        { min: 25, max: 45, bonus: 15 },
        { min: 18, max: 24, bonus: 10 },
        { min: 46, max: 60, bonus: 5 }
      ],
      healthBonus: [
        { status: 'good', bonus: 20 },
        { status: 'average', bonus: 15 }
      ],
      priorityMatch: [
        { priority: 'price', bonus: 25 },
        { priority: 'flexibility', bonus: 20 },
        { priority: 'speed', bonus: 15 }
      ],
      noMedicalExamBonus: 25,
      instantApprovalBonus: 20
    }
  },
  {
    id: 'ethos',
    name: 'Ethos',
    referralLink: 'https://ethoslife.com/quotes',
    description: 'Modern life insurance with simple online application and instant decisions for many applicants.',
    conditions: {
      ageMin: 18,
      ageMax: 65,
      smokingAllowed: true,
      healthStatusRequired: ['excellent', 'good', 'average'],
      noMedicalExamAvailable: true,
      policyTypesOffered: ['term', 'whole', 'not_sure'],
      specialties: ['instant_approval', 'no_medical_exam', 'tech_savvy']
    },
    scoring: {
      baseScore: 80,
      ageBonus: [
        { min: 18, max: 35, bonus: 20 },
        { min: 36, max: 50, bonus: 15 },
        { min: 51, max: 65, bonus: 10 }
      ],
      healthBonus: [
        { status: 'excellent', bonus: 25 },
        { status: 'good', bonus: 20 },
        { status: 'average', bonus: 10 }
      ],
      priorityMatch: [
        { priority: 'speed', bonus: 30 },
        { priority: 'convenience', bonus: 25 },
        { priority: 'price', bonus: 15 }
      ],
      noMedicalExamBonus: 30,
      instantApprovalBonus: 25
    }
  },
  {
    id: 'bestow',
    name: 'Bestow',
    referralLink: 'https://hellobestow.com/apply',
    description: 'Quick online life insurance with instant decisions and competitive rates for healthy applicants.',
    conditions: {
      ageMin: 21,
      ageMax: 55,
      smokingAllowed: false,
      healthStatusRequired: ['excellent', 'good'],
      noMedicalExamAvailable: true,
      policyTypesOffered: ['term'],
      specialties: ['instant_approval', 'no_medical_exam', 'health_conscious']
    },
    scoring: {
      baseScore: 75,
      ageBonus: [
        { min: 21, max: 40, bonus: 25 },
        { min: 41, max: 55, bonus: 15 }
      ],
      healthBonus: [
        { status: 'excellent', bonus: 30 },
        { status: 'good', bonus: 20 }
      ],
      priorityMatch: [
        { priority: 'price', bonus: 30 },
        { priority: 'speed', bonus: 25 },
        { priority: 'simplicity', bonus: 20 }
      ],
      noMedicalExamBonus: 25,
      instantApprovalBonus: 30
    }
  }
];

// Fallback carrier (Ladder Life is the default)
export const FALLBACK_CARRIER_ID = 'ladder-life';

// Helper function to get a carrier by ID
export const getCarrierById = (id: string): AffiliateCarrier | undefined => {
  return AFFILIATE_CARRIERS.find(carrier => carrier.id === id);
};

// Helper function to get fallback carrier
export const getFallbackCarrier = (): AffiliateCarrier => {
  const fallback = getCarrierById(FALLBACK_CARRIER_ID);
  if (!fallback) {
    throw new Error(`Fallback carrier ${FALLBACK_CARRIER_ID} not found in affiliate carriers list`);
  }
  return fallback;
};